import type { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'

import { deriveStatusBadge } from '~/models/planSubmission/statusBadge'

/**
 * 一覧 polling の前回データとの比較で「失敗状態への遷移」を検知する純関数
 * (plansubmit-error-detail design 決定 #4: 一覧のみのトースト通知)。
 *
 * 同一 id の行が「警告なし (hasWarning=false)」→「警告あり
 * (hasWarning=true = 作成失敗/差戻し/提出失敗)」へ遷移した行を返す。
 * - 新規に一覧へ現れた行 (前回データに id が無い) は対象外 (初回ロード /
 *   新規作成直後のノイズを避ける — 新規作成は Generating 開始のため
 *   実際には発生しないが、防御的に除外する)。
 * - 前回・今回とも警告あり、または前回・今回とも警告なしの行は対象外
 *   (遷移していない)。
 *
 * R は `{ id: string, status: PlanSubmissionStatus }` を持つ任意の proto row 型
 * (ProcurementPlanRow / 将来の GenerationSalesPlanRow 等)。
 */
export function detectNewlyFailedRows<
  R extends { id: string; status: PlanSubmissionStatus },
>(previous: R[] | undefined, current: R[]): R[] {
  if (previous === undefined) {
    return []
  }
  const previousWarningById = new Map<string, boolean>(
    previous.map((row) => [row.id, deriveStatusBadge(row.status).hasWarning]),
  )
  return current.filter((row) => {
    const wasWarning = previousWarningById.get(row.id)
    if (wasWarning === undefined) {
      return false
    }
    return !wasWarning && deriveStatusBadge(row.status).hasWarning
  })
}
